import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

export enum ThemeMode {
  Dark = 'dark',
  Light = 'light',
}

type ThemeModeContextValueType = {
  styles: {
    background: string;
    surface: string;
    text: string;
    label: string;
    buttonScheme: string;
    borderColor: string;
  };
  mode: ThemeMode;
  setMode: Dispatch<SetStateAction<ThemeMode>>;
};

const ThemeModeContext = createContext<ThemeModeContextValueType | null>(null);

type Props = {
  children: ReactNode;
};

function ThemeModeProvider({ children }: Props) {
  const [mode, setMode] = useState(ThemeMode.Dark);

  const styles = useMemo(() => {
    switch (mode) {
      case ThemeMode.Dark: {
        return {
          background: '#111',
          surface: '#222',
          text: '#fff',
          label: '#c0c0c0',
          buttonScheme: 'darkButton',
          borderColor: 'gray.700',
        };
      }
      case ThemeMode.Light: {
        return {
          background: '#f6f6fD',
          surface: '#fff',
          text: '#444',
          label: 'gray.600',
          buttonScheme: 'gray',
          borderColor: 'gray.400',
        };
      }
    }
  }, [mode]);

  return (
    <ThemeModeContext.Provider value={{ mode, setMode, styles }}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export const useThemeMode = () => {
  const themeMode = useContext(ThemeModeContext);

  if (!themeMode) {
    throw new Error('Theme mode must be initialized first');
  }

  return themeMode;
};

export default ThemeModeProvider;
